"use client";
import React, { FC, useState } from "react";
import { CardGrid } from "../CardGrid/CardGrid";
import style from "./Articles.module.scss";
import useformatted from "../../../../utils/useformatted";
import { useTranslations } from "next-intl";

interface IProps {
  title: string;
  articles: Array<any>;
  limit?: number;
  subtitle?: boolean;
  itemsPerPage?: number;
}

export const Articles: FC<IProps> = ({
  title,
  articles,
  limit = 0,
  subtitle,
  itemsPerPage = 9,
}) => {
  const translate = useTranslations("author");
  const { formattedDate } = useformatted();
  const [currentPage, setCurrentPage] = useState(1);

  if (!articles || articles.length === 0) return null;

  const totalArticles = limit ? articles.slice(0, limit) : articles;
  const totalPages = Math.ceil(totalArticles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentArticles = totalArticles.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  const getPageNumbers = () => {
    const pages: (number | "...")[] = [];
    const siblingCount = 1;

    if (totalPages <= siblingCount * 2 + 4) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    pages.push(1);

    const ellipsisStart = currentPage > siblingCount + 2;
    const ellipsisEnd = currentPage < totalPages - (siblingCount + 1);

    if (ellipsisStart) {
      pages.push("...");
    }

    const start = Math.max(2, currentPage - siblingCount);
    const end = Math.min(totalPages - 1, currentPage + siblingCount);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (ellipsisEnd) {
      pages.push("...");
    }

    pages.push(totalPages);

    return pages;
  };

  return (
    <section className={style.articlesContainer}>
      <div className="container-blog">
        <h2 className={subtitle ? style.subtitle : ""}>
          {subtitle ? `${translate("title")} ${title}` : title}
        </h2>
        <ul>
          {currentArticles.map((article, index) => (
            <li key={index}>
              <CardGrid
                article={article}
                date={formattedDate(article.attributes.createdAt)}
              />
            </li>
          ))}
        </ul>

        {totalPages > 1 && (
          <div className={style.pagination}>
            <button
              className={style.paginationButton}
              onClick={handlePrevious}
              disabled={currentPage === 1}
              aria-label="Página anterior"
            >
              <svg
                width="13"
                height="10"
                viewBox="0 0 13 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0.162945 5.39151C0.058606 5.28763 5.78737e-08 5.14681 5.96244e-08 5C6.13752e-08 4.85318 0.058606 4.71237 0.162945 4.60849L4.62026 0.17633C4.67126 0.121898 4.73277 0.0782395 4.80112 0.0479594C4.86946 0.0176783 4.94324 0.00139719 5.01805 8.49369e-05C5.09287 -0.00122827 5.16717 0.012456 5.23655 0.0403195C5.30593 0.068184 5.36895 0.109658 5.42186 0.162267C5.47476 0.214876 5.51647 0.277542 5.5445 0.346527C5.57252 0.415513 5.58628 0.489403 5.58496 0.563791C5.58364 0.63818 5.56727 0.711543 5.53681 0.779502C5.50636 0.847462 5.46245 0.908626 5.40771 0.959346L1.9013 4.44598L12.4428 4.44598C12.5906 4.44598 12.7323 4.50435 12.8368 4.60825C12.9413 4.71215 13 4.85306 13 5C13 5.14693 12.9413 5.28785 12.8368 5.39175C12.7323 5.49565 12.5906 5.55402 12.4428 5.55402L1.9013 5.55402L5.40771 9.04065C5.46245 9.09137 5.50636 9.15254 5.53681 9.2205C5.56727 9.28846 5.58364 9.36182 5.58496 9.43621C5.58628 9.5106 5.57252 9.58449 5.5445 9.65347C5.51647 9.72246 5.47476 9.78512 5.42186 9.83773C5.36895 9.89034 5.30593 9.93181 5.23655 9.95968C5.16717 9.98754 5.09286 10.0012 5.01805 9.99991C4.94324 9.9986 4.86946 9.98232 4.80112 9.95204C4.73277 9.92176 4.67126 9.8781 4.62026 9.82367L0.162945 5.39151Z"
                  fill="#17082F"
                />
              </svg>
            </button>

            <div className={style.paginationNumbers}>
              {getPageNumbers().map((page, index) => (
                <React.Fragment key={index}>
                  {page === "..." ? (
                    <span className={style.paginationEllipsis}>...</span>
                  ) : (
                    <button
                      className={`${style.paginationNumber} ${
                        currentPage === page ? style.active : ""
                      }`}
                      onClick={() => handlePageChange(page as number)}
                      aria-label={`Página ${page}`}
                      aria-current={currentPage === page ? "page" : undefined}
                    >
                      {page}
                    </button>
                  )}
                </React.Fragment>
              ))}
            </div>

            <button
              className={style.paginationButton}
              onClick={handleNext}
              disabled={currentPage === totalPages}
              aria-label="Página siguiente"
            >
              <svg
                width="13"
                height="10"
                viewBox="0 0 13 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12.8371 4.60849C12.9414 4.71237 13 4.85319 13 5C13 5.14682 12.9414 5.28763 12.8371 5.39151L8.37974 9.82367C8.32874 9.8781 8.26722 9.92176 8.19888 9.95204C8.13053 9.98232 8.05676 9.9986 7.98195 9.99991C7.90713 10.0012 7.83282 9.98754 7.76345 9.95968C7.69407 9.93182 7.63105 9.89034 7.57814 9.83773C7.52524 9.78512 7.48353 9.72246 7.4555 9.65347C7.42748 9.58449 7.41372 9.5106 7.41504 9.43621C7.41636 9.36182 7.43273 9.28846 7.46319 9.2205C7.49364 9.15254 7.53755 9.09137 7.59229 9.04065L11.0987 5.55402L0.557163 5.55402C0.409394 5.55402 0.267677 5.49565 0.163189 5.39175C0.0587003 5.28785 -2.2498e-07 5.14694 -2.18557e-07 5C-2.12134e-07 4.85307 0.0587004 4.71215 0.163189 4.60825C0.267677 4.50435 0.409394 4.44598 0.557163 4.44598L11.0987 4.44598L7.59229 0.959347C7.53755 0.908627 7.49364 0.847463 7.46319 0.779503C7.43273 0.711544 7.41636 0.638182 7.41504 0.563793C7.41372 0.489405 7.42748 0.415514 7.4555 0.346529C7.48353 0.277543 7.52524 0.214877 7.57814 0.162268C7.63105 0.10966 7.69407 0.0681859 7.76345 0.0403216C7.83283 0.0124574 7.90714 -0.0012265 7.98195 8.60028e-05C8.05676 0.00139851 8.13054 0.0176804 8.19888 0.0479609C8.26723 0.0782414 8.32874 0.1219 8.37974 0.176332L12.8371 4.60849Z"
                  fill="var(--primary)"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
